import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { AlertCircle } from "@/components/icons";
import { supabase } from "@/lib/supabase";
import { FormField } from "./FormField";
import { GoogleButton } from "./GoogleButton";

type Props = { onSuccess: () => void };

export function SigninForm({ onSuccess }: Props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { t } = useTranslation("auth");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const { error: err } = await supabase.auth.signInWithPassword({ email, password });
    setSubmitting(false);
    if (err) {
      setError(err.message);
      return;
    }
    onSuccess();
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <FormField
        label={t("signin.email")}
        type="email"
        autoComplete="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <FormField
        label={t("signin.password")}
        type="password"
        autoComplete="current-password"
        required
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <Button type="submit" className="w-full" disabled={submitting}>
        {submitting ? t("signin.submitting") : t("signin.submit")}
      </Button>
      <div className="relative my-4">
        <Separator />
        <span className="absolute left-1/2 -translate-x-1/2 -top-2.5 bg-card px-2 text-xs text-muted-foreground">
          {t("signin.or")}
        </span>
      </div>
      <GoogleButton />
      <p className="text-sm text-muted-foreground text-center pt-2">
        <Link to="/auth/reset-password" className="underline hover:text-foreground">
          {t("signin.forgotPassword")}
        </Link>
        {" · "}
        <Link to="/auth/signup" className="underline hover:text-foreground">
          {t("signin.signupLink")}
        </Link>
      </p>
    </form>
  );
}
